import {
  modelCatalogueOf,
  panoVariantsOf,
  servedWorldsOf,
  validateLesson,
  type ModelCatalogue,
  type PanoVariants,
  type ServedWorld,
  withCurrentWorldNames,
} from '@vrlearning/lesson-schema';
import type { LoadedLesson } from '../api/lessons-client.js';
import { DeviceRevokedError } from '../api/pairing-client.js';
import { catalogueOfServed } from '../api/served-catalogue.js';
import { cuesFrom } from '../engine/guide.js';

/**
 * Redeeming a `preview.open` ticket (ADR-058): GET /v1/quest-preview/{ticket} with this headset's own
 * token -- the test drive's answer for the item, without its views; or, for a lesson ticket, the lesson
 * version as the school's headsets are served it. Good once; never cached.
 */
export type QuestPreviewAnswer = {
  readonly item: { readonly id: string; readonly kind: 'wereld' | 'object'; readonly name: string };
  readonly media: Readonly<Record<string, string>>;
  readonly variants: Readonly<PanoVariants>;
  readonly models: ModelCatalogue;
  readonly worlds: Readonly<Record<string, ServedWorld>>;
};

export type QuestPreviewOutcome =
  | { kind: 'ready'; answer: QuestPreviewAnswer }
  /** A school's lesson version (Steekproef, ADR-058 amendment), validated as every lesson is. */
  | { kind: 'lesson'; title: string; loaded: LoadedLesson }
  | { kind: 'gone' }
  /** The API said the headset was unlinked: it is being unpaired, and shows the pairing screen. */
  | { kind: 'revoked' }
  | { kind: 'failed'; detail: string };

/** How the request goes out: `deviceFetch` with the stored token in the app, a fake in tests. */
export type PreviewRequest = (path: string, init: RequestInit) => Promise<Response>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const textOf = (value: unknown): string => (typeof value === 'string' ? value : '');

export async function fetchQuestPreview(
  ticket: string,
  request: PreviewRequest,
): Promise<QuestPreviewOutcome> {
  let response: Response;
  try {
    response = await request(`/v1/quest-preview/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch (error) {
    if (error instanceof DeviceRevokedError) return { kind: 'revoked' };
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }
  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord' };
  }
  if (!isRecord(body) || !isRecord(body['item'])) {
    return { kind: 'failed', detail: 'onvolledig antwoord' };
  }
  const item = body['item'];
  const kind = item['kind'];
  if (kind === 'lesson') return lessonOutcome(body, textOf(item['name']));
  if (kind !== 'wereld' && kind !== 'object') {
    return { kind: 'failed', detail: 'geen wereld of object' };
  }
  const media = mediaOf(body);
  return {
    kind: 'ready',
    answer: {
      item: { id: textOf(item['id']), kind, name: textOf(item['name']) },
      media,
      variants: panoVariantsOf(body['variants']),
      models: modelCatalogueOf(body['models']),
      worlds: servedWorldsOf(body['worlds']),
    },
  };
}

/** The signed URLs of an answer, strings only. */
function mediaOf(body: Record<string, unknown>): Record<string, string> {
  return isRecord(body['media'])
    ? Object.fromEntries(
        Object.entries(body['media']).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string',
        ),
      )
    : {};
}

/** A lesson answer, its body validated before it can reach the renderer (as play.ts does). */
function lessonOutcome(body: Record<string, unknown>, title: string): QuestPreviewOutcome {
  const worlds = servedWorldsOf(body['worlds']);
  const catalogue = catalogueOfServed(worlds);
  const result = validateLesson(body['lesson'], { worlds: catalogue });
  if (!result.valid) {
    const first = result.errors[0];
    return {
      kind: 'failed',
      detail: `les is ongeldig: ${first?.path ?? ''} ${first?.message ?? ''}`.trim(),
    };
  }
  return {
    kind: 'lesson',
    title: title || result.lesson.title,
    loaded: {
      // A world's old names played as its current ones (spec 2026-10-16).
      lesson: withCurrentWorldNames(result.lesson, catalogue),
      media: mediaOf(body),
      cues: cuesFrom(body['cues']),
      variants: panoVariantsOf(body['variants']),
      models: modelCatalogueOf(body['models']),
      worlds,
    },
  };
}
